import * as THREE from 'three/webgpu';

/**
 * @file THE PROJECT PALETTE, as data.
 *
 * Every hex in here was lifted from T2's verified look reference — the wall /
 * roof / ground albedos in `engine/dev/scenes/daynight.js`, the warm glow
 * constant in `engine/bake/BakeConfig.js` (`STREET_GLOW`) and the sky stops in
 * `engine/render/sky.js`. That scene IS the art bar; changing these numbers
 * changes the mood of the whole game, so treat them as content, not as code.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THESE VALUES ARE
 * ─────────────────────────────────────────────────────────────────────────────
 * Hex strings in sRGB. `new THREE.Color(hex)` converts them into the renderer's
 * linear working space, which is the space the bake and `GeoBuilder`'s `color`
 * attribute operate in — so ALWAYS go through {@link pickTint} / {@link vary} /
 * {@link toColor} rather than hand-parsing the hex.
 *
 * These are ALBEDOS. The bake multiplies light onto them (see the handoff note
 * at the top of `build/GeoBuilder.js`); nothing here is pre-lit. The one
 * exception is `windows`, which are emissive-shell colours — they are bloom
 * fuel, not surfaces.
 *
 * @module levels/shared/palette
 */

/** @typedef {import('../../engine/types.js').Rng} Rng */

/**
 * @typedef {Object} Palette
 * @property {ReadonlyArray<string>} walls   Facade albedos.
 * @property {ReadonlyArray<string>} roofs   Roof-deck albedos.
 * @property {ReadonlyArray<string>} ground  Streets, dirt, gravel, paving.
 * @property {ReadonlyArray<string>} accents Signage, doors, rust, painted trim.
 *   Used SPARINGLY (a few percent of surfaces) or the palette stops reading as
 *   one place.
 * @property {ReadonlyArray<string>} windows Warm emissive-shell colours.
 */

/**
 * The city.
 *
 * `walls` is T2's `WALL_ALBEDOS` verbatim: seven beiges/greys plus two accents
 * at ~2/9 frequency — exactly enough to break the beige without losing the "one
 * place" read. `roofs` extends T2's three with two darker tar/steel tones for
 * the industrial edge. `ground` starts with the three surfaces the street
 * builder actually paints (asphalt, worn asphalt, concrete) and then carries
 * T2's four earth tones for parks, gravel and the out-of-town skirt.
 *
 * @type {Readonly<Palette>}
 */
export const CITY_PALETTE = Object.freeze({
  walls: Object.freeze([
    '#c8b79c', // pale limestone
    '#b39c88', // warm plaster
    '#a99180', // weathered stucco
    '#a3ab9d', // cold grey-green concrete
    '#cba983', // sun-bleached sandstone
    '#8f877a', // soot-stained grey
    '#c2a58c', // pink-beige render
    '#b0705c', // ACCENT: oxide brick
    '#7d9598', // ACCENT: faded teal panel
  ]),
  roofs: Object.freeze([
    '#7c7266', // tar-and-gravel
    '#6d6a67', // grey felt
    '#8a7d6d', // dusty concrete deck
    '#5d5a57', // dark bitumen (industrial)
    '#8e9295', // galvanised steel
  ]),
  ground: Object.freeze([
    '#5a5754', // asphalt        (street ribbons)
    '#514e4a', // worn asphalt   (boulevard)
    '#8b877f', // concrete       (sidewalk slabs)
    '#b39a72', // T2 GROUND_A — dusty amber earth
    '#8d7f66', // T2 GROUND_B — shaded loam
    '#cbb28a', // T2 GROUND_C — sun-bleached dust
    '#8a8f66', // T2 GROUND_D — dry scrub
  ]),
  accents: Object.freeze([
    '#b0705c', // oxide brick
    '#7d9598', // faded teal
    '#9c5f3a', // rust
    '#c9a227', // hazard yellow
    '#5c6b8a', // municipal blue
    '#8c3f3a', // signage red
  ]),
  windows: Object.freeze([
    '#ffb35c', // = STREET_GLOW [1, 0.7, 0.36] from bake/BakeConfig.js
    '#ffdcaa', // office fluoro
    '#ffc98c', // domestic tungsten
    '#c8d8ff', // rare blue-white (a screen, a shop sign)
  ]),
});

/**
 * THE THREE CITIES' CHARACTER, as albedo lists.
 *
 * ⚠ THESE ARE ADDITIONS, NOT REPLACEMENTS, AND THAT IS DELIBERATE. Every entry
 * of {@link CITY_PALETTE} is load-bearing by INDEX (`config.js` reads
 * `walls[1]`, `roofs[4]`, `ground[6]`…) or WHOLE (`buildings.js` draws an accent
 * from `accents`, `interiors.js` draws a shop's wall from `walls`), so appending
 * a terracotta to `walls` would re-roll every shop interior in the level and
 * changing an entry would move the mood of all three cities at once. A separate
 * table costs nothing and cannot do either.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE THEMES
 * ─────────────────────────────────────────────────────────────────────────────
 * `oldTown` — Kingsmouth. Old-European-meets-modern: fired brick, lime render
 *   and weathered plaster over clay pantile and slate. Warm, and MUTED — the
 *   `brick_a` map carries a strong hue of its own and its `TEX_BOOST` is 3.2
 *   (`levels/city/index.js` says why), so a saturated red authored here comes
 *   out of the multiply as neon.
 * `modern` — Fairview. The baseline read, stated rather than absent: cool
 *   precast concrete, spandrel grey and light render. Nothing warm, nothing
 *   saturated, no accent. It is the town you pass through, and it earns that by
 *   being the one place with no colour opinion.
 * `harbourEast` — Bayside. Chinatown-meets-high-tech: lacquer red and gold
 *   ochre against slate and jade tech panel. The one theme with SATURATED
 *   entries, and they are meant to land at a few percent of the surfaces — the
 *   zone tables in `config.js` mix two or three of these into a majority of
 *   neutrals, which is what keeps a red city from reading as a fire.
 *
 * ⚠ Everything here is an ALBEDO in sRGB, exactly like {@link CITY_PALETTE}, and
 * it reaches the bake through {@link pickTint} / {@link vary} — i.e. through
 * `new THREE.Color(hex)`, which is the same conversion {@link hexToLinear} is.
 * Nothing in this table may be hand-parsed.
 *
 * @type {Readonly<Object<string, {walls: ReadonlyArray<string>, roofs: ReadonlyArray<string>}>>}
 */
export const CITY_THEMES = Object.freeze({
  oldTown: Object.freeze({
    walls: Object.freeze([
      '#b3826a', // fired red brick
      '#c6b190', // lime render
      '#a4886d', // aged plaster
      '#96705a', // dark burnt brick
      '#cdbb9e', // limewash
    ]),
    roofs: Object.freeze([
      '#96604a', // clay pantile
      '#8a5744', // weathered terracotta
      '#6f5b53', // old slate
      '#7d6455', // mossy tile
    ]),
  }),
  modern: Object.freeze({
    walls: Object.freeze([
      '#b6bab9', // pale precast concrete
      '#a3a8ab', // curtain-wall spandrel
      '#c1c2bd', // light render
      '#8d9497', // cool grey panel
    ]),
    roofs: Object.freeze([
      '#76797c', // grey membrane
      '#888d90', // light deck
      '#63676a', // dark membrane
    ]),
  }),
  harbourEast: Object.freeze({
    walls: Object.freeze([
      '#a2463c', // lacquer red
      '#8b3d34', // deep vermillion
      '#bc9450', // gold ochre
      '#5e7076', // jade-grey panel
      '#455060', // dark tech panel
      '#c9b48d', // pale gold render
    ]),
    roofs: Object.freeze([
      '#51565c', // dark tech deck
      '#5e6a62', // jade-tinged deck
      '#6d6154', // ochre-stained deck
    ]),
  }),
});

/**
 * The forest — a STUB. T8 owns the real thing; these exist so
 * `loadForestSet()` has a matching palette to sit beside and so nothing has to
 * invent colours in the meantime.
 *
 * `windows` holds T2's `FOLIAGE_ALBEDOS`, because foliage is to a forest what
 * lit windows are to a city: the one colour decision the whole level hangs on.
 *
 * @type {Readonly<Palette>}
 */
export const FOREST_PALETTE = Object.freeze({
  walls: Object.freeze(['#6a5340', '#7a6249', '#584636']), // bark
  roofs: Object.freeze(['#4f4436', '#6a5b45']),
  ground: Object.freeze(['#6f6a4c', '#8a8f66', '#7a6a4e', '#9b9478']),
  accents: Object.freeze(['#a8563c', '#c9a227', '#4f6e4c']),
  windows: Object.freeze(['#6f8a5a', '#5c7b4f', '#87975c', '#4f6e4c']),
});

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {string|number|THREE.Color} hex
 * @returns {THREE.Color} A NEW colour — callers routinely mutate the result.
 */
export function toColor(hex) {
  return hex instanceof THREE.Color ? hex.clone() : new THREE.Color(hex);
}

/**
 * THE canonical hex → LINEAR albedo triple. Use this — not a hand-rolled
 * `parseInt` — anywhere a hex string becomes a `color` attribute / bake input.
 *
 * A hex is authored in sRGB (the way an artist reads it); the bake and
 * `GeoBuilder` tints operate in LINEAR space, and the difference is ~2.2x of
 * brightness that is invisible until the thing is on screen. Two conventions
 * used to coexist (`props.js#srgb` converted, `forest/config.js#hexRGB` does
 * not — see the note there); this helper is the survivor, and every NEW
 * population of colour-authoring code (creature modules in `game/creatures/`
 * especially) must come through it.
 *
 * @param {string|number} hex e.g. `'#8a7d6d'`.
 * @returns {[number, number, number]} Linear RGB triple, 0..1.
 */
export function hexToLinear(hex) {
  const c = new THREE.Color(hex);
  return [c.r, c.g, c.b];
}

/**
 * Picks one entry from a palette array.
 *
 * Draws EXACTLY ONE value from `rng`, always — the project's stream-stability
 * rule (`math/rng.js`): the number of draws must never depend on the data, or
 * adding one colour to a list reshuffles the rest of the level.
 *
 * @param {Rng} rng
 * @param {ReadonlyArray<string|number>} list
 * @returns {THREE.Color} New colour instance (white when `list` is empty).
 */
export function pickTint(rng, list) {
  const r = rng();
  if (!list || list.length === 0) return new THREE.Color(1, 1, 1);
  const i = Math.min(list.length - 1, Math.floor(r * list.length));
  return toColor(list[i]);
}

/**
 * Per-OBJECT albedo variation: a dominant brightness shift plus a small
 * independent per-channel drift, so two buildings sharing a palette entry do
 * not read as clones.
 *
 * This is NOT `GeoBuilder`'s `tintJitter`, which is per TRIANGLE and exists to
 * break up flat faces. Use both: `vary` picks an object's colour, `tintJitter`
 * gives its facets life.
 *
 * Draws EXACTLY FOUR values from `rng`, always (see {@link pickTint}).
 *
 * @param {Rng} rng
 * @param {string|number|THREE.Color} hex Base colour.
 * @param {number} [amt=0.08] Peak brightness swing, +/- this fraction.
 * @returns {THREE.Color} New colour instance.
 */
export function vary(rng, hex, amt = 0.08) {
  const k = 1 + (rng() * 2 - 1) * amt;
  // The per-channel drift is a third of the brightness swing: enough to stop a
  // row of houses looking machine-stamped, not enough to send one off-palette.
  const c = amt * 0.34;
  const kr = 1 + (rng() * 2 - 1) * c;
  const kg = 1 + (rng() * 2 - 1) * c;
  const kb = 1 + (rng() * 2 - 1) * c;
  const col = toColor(hex);
  col.r = Math.max(0, col.r * k * kr);
  col.g = Math.max(0, col.g * k * kg);
  col.b = Math.max(0, col.b * k * kb);
  return col;
}
